import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline"
import {
  useListAssetsQuery,
  type AssetSourceType,
} from "~/ui-rtk/api/assetsApi"
import type { MegaFeedMediaType } from "~/ui-rtk/api/megaFeedStructureApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import DebouncedInput from "~/ui-rtk/components/ui/controls/DebouncedInput/DebouncedInput"
import useAssetUpload from "~/ui-rtk/hooks/useAssetUpload"

const PAGE_SIZE = 24

type Scope = "product" | "all" | AssetSourceType

const SCOPES: Array<{ value: Scope; label: string }> = [
  { value: "all", label: "All" },
  { value: "UPLOAD", label: "Uploads" },
  { value: "GEN_AI", label: "AI generated" },
  { value: "SHOPIFY_PRODUCT", label: "Shopify" },
  { value: "FACEBOOK_AD", label: "Ads" },
  { value: "INSTAGRAM", label: "Instagram" },
]

export interface PickedAsset {
  id: string
  url: string
}

type Props = {
  isOpen: boolean
  mediaType: MegaFeedMediaType
  title: string
  /** Set → a "This product" scope (the default) lists the product's and its colors' assets, like the drawer. */
  productId?: string
  onClose: () => void
  onSelect: (asset: PickedAsset) => void
}

/**
 * Picks one asset for a structure slot (its example image or fixed asset) or
 * a run cell: from the company's asset library, or uploaded on the spot.
 */
export default function LibraryAssetPicker({
  isOpen,
  mediaType,
  title,
  productId,
  onClose,
  onSelect,
}: Props) {
  const [scope, setScope] = useState<Scope>(productId ? "product" : "all")
  // Reused across run cells: each opening starts on the cell's product.
  useEffect(() => {
    if (isOpen) setScope(productId ? "product" : "all")
  }, [isOpen, productId])
  const scopes: Array<{ value: Scope; label: string }> = productId
    ? [{ value: "product", label: "This product" }, ...SCOPES]
    : SCOPES
  const [search, setSearch] = useState("")
  const [limit, setLimit] = useState(PAGE_SIZE)
  const fileInput = useRef<HTMLInputElement>(null)
  const { uploadFiles, isUploading } = useAssetUpload()

  const { data: library, isFetching } = useListAssetsQuery(
    {
      limit,
      status: "ACTIVE",
      syncStatus: "SYNCED",
      mediaType,
      ...(search ? { search } : {}),
      ...(scope === "product" && productId
        ? { productFamilyRefId: productId }
        : {}),
      ...(scope !== "all" && scope !== "product" ? { sourceType: scope } : {}),
    },
    { skip: !isOpen },
  )
  const assets = (library?.assets ?? []).filter(a => a.url)

  const pick = (asset: PickedAsset) => {
    onSelect(asset)
    onClose()
  }

  const upload = async (file: File) => {
    const [asset] = await uploadFiles([file])
    // The hook falls back to a local blob when the upload fails; that id is
    // not a library asset, so the API would reject it.
    if (!asset || asset.url.startsWith("blob:")) {
      toast.error("Upload failed, try again.")
      return
    }
    pick({ id: asset.id, url: asset.url })
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      closeOnClickOutside
      width={640}
    >
      <div className="flex max-h-[80vh] flex-col rounded-system-md bg-background-dark text-left">
        <div className="flex flex-col gap-3 border-b border-basic-blue p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={isUploading}
                onClick={() => fileInput.current?.click()}
                className="btn btn-xs gap-1 border-none bg-brand text-white hover:bg-brand/80 disabled:opacity-50"
                title="Upload from your computer"
              >
                {isUploading ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <ArrowUpTrayIcon className="h-3 w-3" />
                )}
                Upload
              </button>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <DebouncedInput
            type="search"
            placeholder="Search by file name, product URL or asset id…"
            delay={300}
            defaultValue={search}
            onChange={e => {
              setSearch(e.target.value)
              setLimit(PAGE_SIZE)
            }}
            className="w-full"
          />
          <div className="flex flex-wrap gap-1">
            {scopes.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setScope(option.value)
                  setLimit(PAGE_SIZE)
                }}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  scope === option.value
                    ? "border-brand bg-brand/20 text-white"
                    : "border-basic-blue text-gray-400 hover:border-gray-500 hover:text-white"
                }`}
              >
                {option.label}
              </button>
            ))}
            {library && (
              <span className="ml-auto self-center text-[11px] text-gray-500">
                {library.total} in library
                {search ? ` for “${search}”` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isFetching && assets.length === 0 && (
            <div className="flex h-48 items-center justify-center">
              <span className="loading loading-spinner loading-lg text-brand" />
            </div>
          )}
          {!isFetching && assets.length === 0 && (
            <p className="text-xs text-gray-500">
              No {mediaType === "VIDEO" ? "videos" : "images"} here
              {search ? ` matching “${search}”` : ""}. Try another source, or
              upload.
            </p>
          )}
          <div className="relative">
            {isFetching && assets.length > 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background-dark/60">
                <span
                  aria-label="Loading"
                  className="loading loading-spinner loading-lg text-brand"
                />
              </div>
            )}
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {assets.map(asset => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() =>
                    pick({ id: asset.id, url: asset.url as string })
                  }
                  title="Use this asset"
                  className="aspect-square overflow-hidden rounded-md border border-basic-blue bg-basic-dark-blue hover:border-brand"
                >
                  {asset.thumbnailUrl || mediaType === "IMAGE" ? (
                    <img
                      src={(asset.thumbnailUrl ?? asset.url) as string}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <video
                      src={asset.url}
                      muted
                      className="h-full w-full object-cover"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
          {library && library.total > limit && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                className="btn btn-ghost btn-xs gap-1"
                disabled={isFetching}
                onClick={() => setLimit(l => l + PAGE_SIZE)}
              >
                {isFetching && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                {isFetching
                  ? "Loading…"
                  : `Show more (${library.total - limit} left)`}
              </button>
            </div>
          )}
        </div>

        <input
          ref={fileInput}
          type="file"
          accept={mediaType === "IMAGE" ? "image/*" : "video/*"}
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) void upload(file)
            e.target.value = ""
          }}
        />
      </div>
    </Dialog>
  )
}
